export const HIT_CLASS = 'hs-search-hit';
export const HIT_WORD_CLASS = 'hs-search-hit-word';
export const HIT_ICON_CLASS = 'hs-search-hit-icon';

export function normalizeQuery(value) {
  return String(value || '').trim();
}

export function queryMatches(text, query) {
  const needle = normalizeQuery(query).toLowerCase();
  if (!needle) {
    return false;
  }

  return String(text || '').toLowerCase().indexOf(needle) !== -1;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isTokenChar(ch) {
  return /[0-9A-Za-z\u00C0-\u024F\u0400-\u04FF'-]/.test(ch);
}

function expandToToken(source, index, length) {
  let start = index;
  let end = index + length;

  while (start > 0 && isTokenChar(source.charAt(start - 1))) {
    start -= 1;
  }

  while (end < source.length && isTokenChar(source.charAt(end))) {
    end += 1;
  }

  return { start: start, end: end };
}

function collectMatchRanges(source, needle) {
  const pattern = new RegExp(escapeRegExp(needle), 'ig');
  const ranges = [];
  let match = pattern.exec(source);

  while (match) {
    ranges.push({ start: match.index, end: match.index + match[0].length });
    match = pattern.exec(source);
  }

  return ranges;
}

function collectWrappedHits(source, needle) {
  const matches = collectMatchRanges(source, needle);
  if (!matches.length) {
    return [];
  }

  const groups = [];
  for (let i = 0; i < matches.length; i += 1) {
    const hit = matches[i];
    const token = expandToToken(source, hit.start, hit.end - hit.start);
    const prev = groups.length ? groups[groups.length - 1] : null;
    if (prev && token.start <= prev.tokenEnd) {
      prev.tokenEnd = Math.max(prev.tokenEnd, token.end);
      prev.marks.push(hit);
    } else {
      groups.push({
        tokenStart: token.start,
        tokenEnd: token.end,
        marks: [hit],
      });
    }
  }

  return groups;
}

export function clearHighlights(root) {
  if (!root) {
    return;
  }

  const wraps = root.querySelectorAll('span.' + HIT_WORD_CLASS);
  for (let w = 0; w < wraps.length; w += 1) {
    const wrap = wraps[w];
    const parent = wrap.parentNode;
    if (!parent) {
      continue;
    }

    parent.replaceChild(document.createTextNode(wrap.textContent || ''), wrap);
    parent.normalize();
  }

  const marks = root.querySelectorAll('mark.' + HIT_CLASS);
  for (let i = 0; i < marks.length; i += 1) {
    const mark = marks[i];
    const parent = mark.parentNode;
    if (!parent) {
      continue;
    }

    parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
    parent.normalize();
  }

  const icons = root.querySelectorAll('.' + HIT_ICON_CLASS);
  for (let j = 0; j < icons.length; j += 1) {
    icons[j].classList.remove(HIT_ICON_CLASS);
  }
}

function highlightTextNode(node, query) {
  const needle = normalizeQuery(query);
  if (!needle || !node || node.nodeType !== 1) {
    return;
  }

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
  const targets = [];

  while (walker.nextNode()) {
    const textNode = walker.currentNode;
    if (textNode.nodeValue && queryMatches(textNode.nodeValue, needle)) {
      targets.push(textNode);
    }
  }

  for (let i = 0; i < targets.length; i += 1) {
    const textNode = targets[i];
    const source = textNode.nodeValue || '';
    const groups = collectWrappedHits(source, needle);
    if (!groups.length) {
      continue;
    }

    const frag = document.createDocumentFragment();
    let lastIndex = 0;

    for (let g = 0; g < groups.length; g += 1) {
      const group = groups[g];
      if (group.tokenStart > lastIndex) {
        frag.appendChild(document.createTextNode(source.slice(lastIndex, group.tokenStart)));
      }

      const wrap = document.createElement('span');
      wrap.className = HIT_WORD_CLASS;
      let cursor = group.tokenStart;

      for (let m = 0; m < group.marks.length; m += 1) {
        const hit = group.marks[m];
        if (hit.start > cursor) {
          wrap.appendChild(document.createTextNode(source.slice(cursor, hit.start)));
        }

        const mark = document.createElement('mark');
        mark.className = HIT_CLASS;
        mark.textContent = source.slice(hit.start, hit.end);
        wrap.appendChild(mark);
        cursor = hit.end;
      }

      if (cursor < group.tokenEnd) {
        wrap.appendChild(document.createTextNode(source.slice(cursor, group.tokenEnd)));
      }

      frag.appendChild(wrap);
      lastIndex = group.tokenEnd;
    }

    if (lastIndex < source.length) {
      frag.appendChild(document.createTextNode(source.slice(lastIndex)));
    }

    if (textNode.parentNode) {
      textNode.parentNode.replaceChild(frag, textNode);
    }
  }
}

export function highlightInElement(element, query) {
  if (!element) {
    return;
  }

  highlightTextNode(element, query);
}

export function highlightCardText(card, query, selectors) {
  if (!card) {
    return;
  }

  clearHighlights(card);

  const needle = normalizeQuery(query);
  if (!needle) {
    return;
  }

  const nodes = card.querySelectorAll(selectors);
  for (let i = 0; i < nodes.length; i += 1) {
    highlightInElement(nodes[i], needle);
  }
}

function panelBiomarkerText(panel) {
  if (!panel) {
    return '';
  }

  const links = panel.querySelectorAll('[data-testid="hs-card-group-test-link"]');
  const parts = [];
  for (let i = 0; i < links.length; i += 1) {
    parts.push(links[i].textContent || '');
  }

  return parts.join(' ');
}

export function applyCollapsedGroupHits(card, query) {
  const root = card && card.querySelector('[data-hs-card-groups]');
  if (!root || root.classList.contains('hs-card-groups--single')) {
    return;
  }

  const needle = normalizeQuery(query);
  if (!needle) {
    return;
  }

  const icons = root.querySelectorAll('[data-testid="hs-card-group-icon"]');
  const matched = [];

  for (let i = 0; i < icons.length; i += 1) {
    const icon = icons[i];
    const groupId = icon.getAttribute('data-hs-card-group-id') || '';
    const panel = root.querySelector(
      '[data-testid="hs-card-group-panel"][data-hs-card-group-id="' + groupId + '"]'
    );

    if (queryMatches(panelBiomarkerText(panel), needle)) {
      matched.push({ icon: icon, panel: panel, groupId: groupId });
      icon.classList.add(HIT_ICON_CLASS);
    }
  }

  if (matched.length === 1 && matched[0].panel) {
    const hit = matched[0];
    hit.icon.classList.add('is-open');
    hit.icon.setAttribute('aria-expanded', 'true');
    hit.panel.hidden = false;
    root.setAttribute('data-hs-search-opened', '1');
    highlightCardText(hit.panel, needle, '[data-testid="hs-card-group-test-link"]');
  }
}

export function resetSearchOpenedGroups(root) {
  if (!root) {
    return;
  }

  const opened = root.querySelectorAll('[data-hs-search-opened="1"]');
  for (let i = 0; i < opened.length; i += 1) {
    const groups = opened[i];
    groups.removeAttribute('data-hs-search-opened');
    groups.classList.remove('is-showing-all');

    const icons = groups.querySelectorAll('[data-testid="hs-card-group-icon"]');
    for (let j = 0; j < icons.length; j += 1) {
      icons[j].classList.remove('is-open', HIT_ICON_CLASS);
      icons[j].setAttribute('aria-expanded', 'false');
    }

    const panels = groups.querySelectorAll('[data-testid="hs-card-group-panel"]');
    for (let k = 0; k < panels.length; k += 1) {
      panels[k].hidden = true;
    }

    const showAll = groups.querySelector('[data-testid="hs-card-group-show-all"]');
    if (showAll) {
      showAll.classList.remove('is-open');
      showAll.setAttribute('aria-expanded', 'false');
      showAll.textContent = groups.getAttribute('data-show-all-label') || 'Show all';
    }
  }
}
